/**
 * MapBackgroundLayer — V14.68.6 "Vision Map Partout" (Feb 2026)
 *
 * Rend une carte Leaflet fixée en `position: fixed; inset: 0; z-index: 0`
 * qui sert de fond visuel à toutes les pages authentifiées.
 *
 * Caractéristiques :
 *   - Tiles satellite Esri World Imagery (cohérent avec Sprint 32).
 *   - Non-interactive (dragging, zoom, wheel désactivés) pour ne pas
 *     entrer en conflit avec les inputs des pages sur le dessus.
 *   - Suit le contexte `MapBackgroundContext` : recentre via flyTo à
 *     chaque changement de `center` / `zoom`.
 *   - Masquée si `!visible` ou si une page a appelé `useSuppressMapBackground`.
 */
import { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { useMapBackground } from '../contexts/MapBackgroundContext';

function MapController({ center, zoom }) {
  const map = useMap();
  const initRef = useRef(false);

  useEffect(() => {
    if (!map) return;
    if (!initRef.current) {
      map.setView(center, zoom, { animate: false });
      initRef.current = true;
    } else {
      map.flyTo(center, zoom, { duration: 1.2 });
    }
  }, [center?.[0], center?.[1], zoom, map]);

  // Invalidate size when window resizes (parent overlay may change)
  useEffect(() => {
    if (!map) return;
    const onResize = () => map.invalidateSize();
    window.addEventListener('resize', onResize);
    // small initial nudge — leaflet often needs it after mount
    const t = setTimeout(() => map.invalidateSize(), 250);
    return () => {
      window.removeEventListener('resize', onResize);
      clearTimeout(t);
    };
  }, [map]);

  return null;
}

export default function MapBackgroundLayer() {
  const { center, zoom, visible, suppressed } = useMapBackground();

  if (!visible || suppressed) return null;

  return (
    <div
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: 0 }}
      data-testid="map-background-layer"
      aria-hidden="true"
    >
      <MapContainer
        center={center}
        zoom={zoom}
        className="w-full h-full"
        style={{ background: '#09090B' }}
        zoomControl={false}
        attributionControl={false}
        dragging={false}
        scrollWheelZoom={false}
        doubleClickZoom={false}
        touchZoom={false}
        boxZoom={false}
        keyboard={false}
        preferCanvas
      >
        <TileLayer
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          attribution="Tiles © Esri"
          maxZoom={19}
        />
        <MapController center={center} zoom={zoom} />
      </MapContainer>
      {/* Subtle overlay for depth + to slightly darken raw imagery */}
      <div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse 90% 70% at 50% 50%, rgba(9,9,11,0.25) 0%, rgba(9,9,11,0.55) 70%, rgba(9,9,11,0.75) 100%)',
        }}
      />
    </div>
  );
}
